import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { fetchJson } from "../lib/api";
import { apiBase } from "../lib/apiBase";
import { useAuth } from "../lib/AuthContext";
import { resolveCanonicalSymbol, sortCanonicalSymbols } from "../utils/coinSymbols";

const MIN_ENGINE_ALLOC = 0.15;
const EPSILON = 1e-6;
const ENGINE_KEYS = ["dream", "scalp", "horizon"];
const SCALP_DISABLED_SYMBOLS = new Set(["ADA", "HBAR"]);
const PRESENT_ENGINE_KEYS = "__present_engine_keys";

function normalizeValueToUnit(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return undefined;
    if (num > 1 + EPSILON) return num / 100;
    return num;
}

function formatPercent(value) {
    if (value == null || Number.isNaN(value)) return "–";
    return `${(Number(value) * 100).toFixed(1)}%`;
}

function normalizePinConfig() {
    const runtimeEnv = globalThis.__IMPORT_META_ENV__ ?? {};
    const envPin = (runtimeEnv?.VITE_SAFETY_CONFIRM_PIN ?? "").trim();
    if (envPin) return envPin;

    const legacyPin = (typeof globalThis !== "undefined" && globalThis.__SAFETY_CONFIRM_PIN__) || "";
    return String(legacyPin || "").trim();
}

function normalizeEngineList(list) {
    if (!Array.isArray(list)) return [];
    return list
        .map((engine) => String(engine || "").trim().toLowerCase())
        .map((engine) => (engine === "scalping" ? "scalp" : engine))
        .filter((engine, idx, arr) => ENGINE_KEYS.includes(engine) && arr.indexOf(engine) === idx);
}

function presentEngineKeys(entry = {}) {
    return Array.isArray(entry[PRESENT_ENGINE_KEYS])
        ? entry[PRESENT_ENGINE_KEYS]
        : ENGINE_KEYS.filter((engine) => Object.prototype.hasOwnProperty.call(entry, engine));
}

function metadataActiveEngines(entry = {}) {
    return normalizeEngineList(entry.active_engines || entry.enabled_engines || entry.activeEngines);
}

function activeEnginesForEntry(coin, entry = {}, fallbackEntry = {}) {
    const metadataActive = metadataActiveEngines(entry);
    if (metadataActive.length > 0) return metadataActive;

    const fallbackActive = metadataActiveEngines(fallbackEntry);
    if (fallbackActive.length > 0) return fallbackActive;

    const disabled = new Set([
        ...normalizeEngineList(fallbackEntry.disabled_engines || fallbackEntry.disabledEngines),
        ...normalizeEngineList(entry.disabled_engines || entry.disabledEngines),
    ]);
    if (SCALP_DISABLED_SYMBOLS.has(String(coin || "").toUpperCase())) disabled.add("scalp");

    const present = presentEngineKeys(entry);
    const presentActive = present.filter((engine) => !disabled.has(engine));
    const fallbackPresentActive = presentEngineKeys(fallbackEntry).filter((engine) => !disabled.has(engine));

    if (fallbackPresentActive.length > presentActive.length) return fallbackPresentActive;
    if (presentActive.length > 0) return presentActive;
    if (disabled.size > 0) return ENGINE_KEYS.filter((engine) => !disabled.has(engine));
    return ENGINE_KEYS;
}

function validateAllocations(map = {}, fallbackMap = {}) {
    const coins = Object.keys(map);
    const invalid = [];
    coins.forEach((coin) => {
        const entry = map[coin] || {};
        const fallbackEntry = fallbackMap[coin] || fallbackMap[String(coin).toUpperCase()] || {};
        const activeEngines = activeEnginesForEntry(coin, entry, fallbackEntry);
        const values = [];

        for (const engine of ENGINE_KEYS) {
            if (!Object.prototype.hasOwnProperty.call(entry, engine) || entry[engine] == null) continue;
            const numeric = Number(entry[engine]);
            if (!Number.isFinite(numeric) || numeric < -EPSILON) {
                invalid.push({ coin, reason: `Invalid numeric allocation for ${engine}` });
                return;
            }
        }

        for (const engine of activeEngines) {
            const value = entry[engine];
            if (value == null) {
                invalid.push({ coin, reason: `Missing active engine allocation for ${engine}` });
                return;
            }
            const numeric = Number(value);
            if (!Number.isFinite(numeric)) {
                invalid.push({ coin, reason: `Invalid numeric allocation for ${engine}` });
                return;
            }
            if (numeric < MIN_ENGINE_ALLOC - EPSILON) {
                invalid.push({ coin, reason: `Each active engine requires at least ${Math.round(MIN_ENGINE_ALLOC * 100)}%` });
                return;
            }
            values.push(numeric);
        }

        const total = values.reduce((acc, v) => acc + v, 0);
        if (Math.abs(total - 1) > EPSILON) {
            invalid.push({ coin, reason: "Active engine allocations must sum to 100%" });
        }
    });
    return invalid;
}

function normalizeAllocationMap(map = {}) {
    const merged = {};
    Object.entries(map || {}).forEach(([key, value]) => {
        const canonicalKey = resolveCanonicalSymbol(value?.canonical_symbol || value?.symbol || key);
        if (!canonicalKey) return;
        const existing = merged[canonicalKey] || {};
        const present = new Set(existing[PRESENT_ENGINE_KEYS] || []);
        ENGINE_KEYS.forEach((engine) => {
            if (Object.prototype.hasOwnProperty.call(value || {}, engine)) present.add(engine);
        });
        const dream = normalizeValueToUnit(existing?.dream ?? value?.dream);
        const scalp = normalizeValueToUnit(existing?.scalp ?? value?.scalp);
        const horizon = normalizeValueToUnit(existing?.horizon ?? value?.horizon);
        const normalized = {
            ...existing,
            ...value,
            symbol: canonicalKey,
            canonical_symbol: canonicalKey,
            dream,
            horizon,
        };
        if (Object.prototype.hasOwnProperty.call(value || {}, "scalp") || Object.prototype.hasOwnProperty.call(existing, "scalp")) {
            normalized.scalp = scalp;
        }
        Object.defineProperty(normalized, PRESENT_ENGINE_KEYS, {
            value: [...present],
            enumerable: false,
            configurable: true,
        });
        merged[canonicalKey] = normalized;
    });
    return merged;
}

function buildAllocationPayload(map = {}) {
    const payload = {};
    Object.entries(map || {}).forEach(([coin, entry]) => {
        if (!entry || typeof entry !== "object") return;
        const clean = { symbol: entry.symbol || coin, canonical_symbol: entry.canonical_symbol || coin };
        ENGINE_KEYS.forEach((engine) => {
            if (Object.prototype.hasOwnProperty.call(entry, engine) && entry[engine] !== undefined) {
                clean[engine] = entry[engine];
            }
        });
        payload[coin] = clean;
    });
    return payload;
}

export default function EngineAllocationPanel({ className = "", initialAllocations = null, initialSuggestions = null }) {
    const { logout } = useAuth();
    const [allocations, setAllocations] = useState(initialAllocations || {});
    const [suggestions, setSuggestions] = useState(initialSuggestions);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [suggestLoading, setSuggestLoading] = useState(false);
    const [applyLoading, setApplyLoading] = useState(false);
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [pinModalOpen, setPinModalOpen] = useState(false);
    const [pinValue, setPinValue] = useState("");
    const [pinError, setPinError] = useState("");

    const configuredPin = useMemo(() => normalizePinConfig(), []);
    const pinRequired = configuredPin.length > 0;

    const handleAuthFailure = useCallback(() => {
        logout({ skipRequest: true });
        setError("Session expired. Please sign in again.");
    }, [logout]);

    const loadAllocations = useCallback(async () => {
        if (initialAllocations) {
            return;
        }

        setLoading(true);
        setError("");
        try {
            const res = await fetchJson(apiBase("engine_allocations"));
            if (res.unauth) {
                handleAuthFailure();
                return;
            }
            if (res.ok) {
                setAllocations(res.data?.allocations || {});
            } else {
                setError("Unable to load engine allocations.");
                setAllocations(res.data?.allocations || {});
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            setError("Unable to load engine allocations.");
        } finally {
            setLoading(false);
        }
    }, [handleAuthFailure]);

    const loadStatus = useCallback(async () => {
        try {
            const res = await fetchJson(apiBase("engine_allocations/maintenance/status"));
            if (res.unauth) {
                handleAuthFailure();
                return;
            }
            if (res.ok && res.data?.state?.last_run) {
                setStatus(res.data);
            }
        } catch {
            // ignore optional status failure
        }
    }, [handleAuthFailure]);

    useEffect(() => {
        if (initialAllocations) {
            setLoading(false);
        } else {
            loadAllocations();
        }
        loadStatus();
    }, [initialAllocations, loadAllocations, loadStatus]);

    const fetchSuggestions = useCallback(async () => {
        setSuggestLoading(true);
        setError("");
        setMessage("");
        try {
            const res = await fetchJson(apiBase("engine_allocations/suggestions"));
            if (res.unauth) {
                handleAuthFailure();
                return;
            }
            if (!res.ok) {
                setError(res.data?.error || res.data?.message || "Unable to fetch suggestions.");
                return;
            }
            setSuggestions(res.data || null);
            setSuggestionsOpen(true);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            setError("Unable to fetch suggestions.");
        } finally {
            setSuggestLoading(false);
        }
    }, [handleAuthFailure]);

    const normalizedAllocations = useMemo(() => normalizeAllocationMap(allocations), [allocations]);
    const suggestedAllocations = useMemo(
        () => normalizeAllocationMap(suggestions?.suggested || null),
        [suggestions?.suggested],
    );
    const suggestionEnabled = Boolean(suggestions?.enabled);
    const suggestionExperimental = Boolean(suggestions?.experimental);
    const suggestionWarning = suggestions?.warning;
    const changedCoinsFromApi = useMemo(
        () =>
            new Set(
                (suggestions?.changed_coins || [])
                    .map((c) => resolveCanonicalSymbol(c) || String(c || "").toUpperCase())
                    .filter(Boolean),
            ),
        [suggestions?.changed_coins],
    );
    const validationErrors = useMemo(
        () => validateAllocations(suggestedAllocations || {}, normalizedAllocations || {}),
        [normalizedAllocations, suggestedAllocations],
    );
    const hasSuggestions = useMemo(
        () => Object.keys(suggestedAllocations || {}).length > 0,
        [suggestedAllocations],
    );

    const coins = useMemo(() => {
        const ordered = new Set();
        Object.keys(normalizedAllocations || {}).forEach((key) => ordered.add(key));
        Object.keys(suggestedAllocations || {}).forEach((key) => ordered.add(key));
        return sortCanonicalSymbols([...ordered]);
    }, [normalizedAllocations, suggestedAllocations]);

    const suggestionDiffs = useMemo(() => {
        const diff = new Set();
        if (!suggestedAllocations) return diff;
        coins.forEach((coin) => {
            const current = normalizedAllocations?.[coin] || normalizedAllocations?.[coin.toUpperCase()] || {};
            const suggestion = suggestedAllocations[coin];
            if (!suggestion) return;
            const changed = ["dream", "scalp", "horizon"].some((eng) => {
                const target = Number(suggestion[eng] ?? 0);
                const base = Number(current[eng] ?? 0);
                return Math.abs(target - base) > EPSILON;
            });
            if (changed) diff.add(coin.toUpperCase());
        });
        return diff;
    }, [coins, normalizedAllocations, suggestedAllocations]);

    const hasPendingSuggestions = hasSuggestions && (suggestionDiffs.size > 0 || changedCoinsFromApi.size > 0);

    const allocationErrorMessage = useCallback((res, fallback = "Failed to apply suggestions.") => {
        if (res?.httpStatus === 401 || res?.status === "unauthorized") {
            return "Unauthorized. Please sign in again before changing engine allocations.";
        }
        if (res?.httpStatus === 403 || res?.status === "forbidden") {
            return "Forbidden. Admin authorization and a valid CSRF token are required to change engine allocations.";
        }
        return res?.data?.detail || res?.data?.error || res?.status || fallback;
    }, []);

    const applyConfirmedAllocations = useCallback((res) => {
        const confirmed = res?.data?.allocations;
        if (!confirmed || typeof confirmed !== "object" || Array.isArray(confirmed)) {
            setError("Backend did not confirm persisted engine allocations.");
            return false;
        }
        setAllocations(confirmed);
        setMessage("Engine allocations updated.");
        return true;
    }, []);

    const resetPinModal = useCallback(() => {
        setPinModalOpen(false);
        setPinValue("");
        setPinError("");
    }, []);

    const validatePin = useCallback(
        (inputPin) => {
            if (!pinRequired) return { valid: true };
            const normalized = String(inputPin ?? "").trim();
            if (!normalized) return { valid: false, error: "PIN is required." };
            if (!/^[0-9]+$/.test(normalized)) return { valid: false, error: "PIN must contain digits only." };
            if (normalized !== configuredPin) {
                return { valid: false, error: "Incorrect PIN. Try again." };
            }
            return { valid: true };
        },
        [configuredPin, pinRequired],
    );

    const applySuggestions = useCallback(async () => {
        if (!hasSuggestions) {
            setError("No suggestions to apply.");
            return;
        }
        if (validationErrors.length > 0) {
            setError(validationErrors[0].reason);
            return;
        }

        if (pinRequired) {
            setPinModalOpen(true);
            setPinValue("");
            setPinError("");
            return;
        }

        setApplyLoading(true);
        setError("");
        setMessage("");
        try {
            const res = await fetchJson(apiBase("engine_allocations"), {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ allocations: buildAllocationPayload(suggestedAllocations) }),
            });
            if (!res.ok) {
                setError(allocationErrorMessage(res));
                return;
            }
            applyConfirmedAllocations(res);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            setError("Failed to apply suggestions.");
        } finally {
            setApplyLoading(false);
        }
    }, [allocationErrorMessage, applyConfirmedAllocations, hasSuggestions, pinRequired, suggestedAllocations, validationErrors]);

    const confirmPinAndApply = useCallback(async () => {
        if (!hasSuggestions) {
            resetPinModal();
            return;
        }
        const result = validatePin(pinValue);
        if (!result.valid) {
            setPinError(result.error || "Incorrect PIN. Please try again.");
            return;
        }
        resetPinModal();
        setApplyLoading(true);
        setError("");
        setMessage("");
        try {
            const res = await fetchJson(apiBase("engine_allocations"), {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ allocations: buildAllocationPayload(suggestedAllocations) }),
            });
            if (!res.ok) {
                setError(allocationErrorMessage(res));
                return;
            }
            applyConfirmedAllocations(res);
        } catch {
            setError("Failed to apply suggestions.");
        } finally {
            setApplyLoading(false);
        }
    }, [allocationErrorMessage, applyConfirmedAllocations, hasSuggestions, pinValue, resetPinModal, suggestedAllocations, validatePin]);

    return (
        <div className={`space-y-3 ${className}`} data-testid="engine-allocation-panel">
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-[0.65rem] uppercase tracking-wide text-gray-500">Engine allocations</p>
                    <h3 className="text-lg font-semibold text-white">Engine Allocations</h3>
                    <p className="text-sm text-gray-300">
                        Review per-coin engine allocations and apply backend suggestions.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <button
                        type="button"
                        onClick={() => setSuggestionsOpen((prev) => !prev)}
                        className="px-3 py-1 text-sm rounded bg-gray-800 text-gray-100 border border-gray-700 hover:border-cyan-500"
                        data-testid="toggle-suggestions"
                    >
                        {suggestionsOpen ? "Hide suggestions" : "Show suggestions"}
                    </button>
                    <button
                        type="button"
                        onClick={loadAllocations}
                        className="px-3 py-1 text-sm rounded bg-gray-800 text-gray-100 border border-gray-700 hover:border-cyan-500"
                    >
                        Refresh
                    </button>
                    <button
                        type="button"
                        onClick={fetchSuggestions}
                        disabled={suggestLoading}
                        className="px-3 py-1 text-sm rounded bg-blue-700 text-white hover:bg-blue-600 disabled:opacity-60"
                        data-testid="fetch-suggestions"
                    >
                        {suggestLoading ? "Fetching..." : "Fetch suggestions"}
                    </button>
                    <button
                        type="button"
                        onClick={applySuggestions}
                        disabled={applyLoading || !hasPendingSuggestions}
                        className="px-3 py-1 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
                        data-testid="apply-suggestions"
                    >
                        {applyLoading ? "Applying..." : "Apply suggestions"}
                    </button>
                </div>
            </div>
            {validationErrors.length > 0 && (
                <span data-testid="validation-warning" className="text-xs text-red-600">
                    {validationErrors[0].reason}
                </span>
            )}
            {loading ? (
                <div data-testid="engine-allocation-loading" className="text-sm text-gray-500">
                    Loading allocations...
                </div>
            ) : (
                <div className="overflow-auto border border-gray-800 rounded-md">
                    <table className="min-w-full divide-y divide-gray-800">
                        <thead className="bg-gray-900/60">
                            <tr className="text-left text-xs font-semibold text-gray-200">
                                <th className="py-2 px-3">Coin</th>
                                <th className="py-2 px-3 text-right">Dream</th>
                                <th className="py-2 px-3 text-right">Scalping</th>
                                <th className="py-2 px-3 text-right">Horizon</th>
                                <th className="py-2 px-3 text-right">Suggested</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                            {coins.map((coin) => {
                                const allocation = normalizedAllocations?.[coin];
                                const suggestion = suggestedAllocations?.[coin];
                                const isChanged = suggestionDiffs.has(coin) || changedCoinsFromApi.has(coin);
                                const missing = !allocation && !suggestion;
                                const rowClass = isChanged ? "bg-amber-500/10" : "";
                                return (
                                    <tr key={coin} className={rowClass}>
                                        <td className="py-2 px-3 font-semibold text-white">{coin}</td>
                                        <td className="py-2 px-3 text-right text-gray-100">
                                            {formatPercent(allocation?.dream)}
                                        </td>
                                        <td className="py-2 px-3 text-right text-gray-100">
                                            {formatPercent(allocation?.scalp)}
                                        </td>
                                        <td className="py-2 px-3 text-right text-gray-100">
                                            {formatPercent(allocation?.horizon)}
                                        </td>
                                        <td className="py-2 px-3 text-right text-xs">
                                            {!suggestionsOpen ? (
                                                <span className="text-gray-600 italic">Expand suggestions to view</span>
                                            ) : suggestion ? (
                                                <span className="inline-flex items-center gap-1">
                                                    {`${formatPercent(suggestion.dream)} / ${formatPercent(suggestion.scalp)} / ${formatPercent(suggestion.horizon)}`}
                                                    {isChanged && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] bg-amber-300/90 text-amber-900">
                                                            diff
                                                        </span>
                                                    )}
                                                </span>
                                            ) : missing ? (
                                                <span className="text-gray-500">Not configured</span>
                                            ) : (
                                                <span className="text-gray-500">—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {coins.length === 0 && (
                                <tr>
                                    <td className="py-3 px-3 text-sm text-gray-400" colSpan={5}>
                                        No allocations configured.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            {status?.state?.last_run && (
                <span className="text-xs text-gray-600" data-testid="maintenance-status">
                    Last refresh: {new Date(status.state.last_run).toLocaleDateString()} | Next run: {status.state.next_run
                        ? new Date(status.state.next_run).toLocaleDateString()
                        : "pending"}
                </span>
            )}
            {suggestionsOpen && (
                <div className="space-y-2">
                    {suggestionExperimental && (
                        <div className="flex flex-wrap items-center gap-2 text-xs text-amber-200" data-testid="suggestion-warning">
                            <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-[0.65rem] uppercase border border-amber-400/40">
                                Experimental
                            </span>
                            <span>{suggestionWarning || "These suggestions are not production-grade."}</span>
                        </div>
                    )}
                    {!suggestionEnabled && suggestionWarning && (
                        <div className="text-xs text-gray-400" data-testid="suggestion-disabled-stub">
                            {suggestionWarning}
                        </div>
                    )}
                    {suggestions?.generated_at && (
                        <div className="text-xs text-gray-600" data-testid="suggestion-meta">
                            Suggestions generated {new Date(suggestions.generated_at).toLocaleString()}
                        </div>
                    )}
                </div>
            )}
            {message && (
                <div className="text-sm text-green-700" data-testid="engine-allocation-success">
                    {message}
                </div>
            )}
            {error && (
                <div className="text-sm text-red-700" data-testid="engine-allocation-error">
                    {error}
                </div>
            )}
            {pinModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-20">
                    <div className="bg-white rounded shadow-lg p-4 w-80">
                        <h3 className="text-base font-semibold mb-2">Enter PIN to apply</h3>
                        <p className="text-xs text-gray-600 mb-3">
                            Applying suggested allocations requires confirmation.
                        </p>
                        <label htmlFor="engine-pin" className="text-xs text-gray-700">
                            PIN
                        </label>
                        <input
                            id="engine-pin"
                            data-testid="engine-pin-input"
                            type="password"
                            value={pinValue}
                            onChange={(e) => setPinValue(e.target.value)}
                            className="w-full border rounded px-2 py-1 mt-1"
                        />
                        {pinError && (
                            <div data-testid="engine-pin-error" className="text-xs text-red-600 mt-1">
                                {pinError}
                            </div>
                        )}
                        <div className="mt-3 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={resetPinModal}
                                className="px-3 py-1 text-sm rounded bg-gray-200 hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmPinAndApply}
                                className="px-3 py-1 text-sm rounded bg-green-600 text-white hover:bg-green-700"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

EngineAllocationPanel.propTypes = {
    className: PropTypes.string,
    initialAllocations: PropTypes.object,
    initialSuggestions: PropTypes.object,
};
